/**
 * Overview (spec section 58).
 *
 * Two things are load-bearing on this screen:
 *
 * - The growth cards are inference, not observation. They are labelled and
 *   carry the sample count the server computed them from, because "+8.3M in
 *   24h" derived from two snapshots taken 40 minutes apart is not a daily
 *   growth rate.
 * - Warnings are shown above the numbers, not beneath them. The point of the
 *   screen is to surface what is wrong before the operator starts planning.
 */

import { Link } from 'react-router-dom';
import type { DashboardResponse } from '@kso/schemas';
import { useDashboard } from '../../api/queries';
import {
  Badge,
  Card,
  Notice,
  PageHeader,
  Stat,
  Value,
} from '../../components/ui/primitives';
import { EmptyState, QueryBoundary } from '../../components/ui/states';
import { FreshnessBadge } from '../../components/freshness/Freshness';
import { InferenceMark, SampleCount } from '../../components/inference/Inference';
import { formatCompact, formatDateTime, formatDelta, formatNumber } from '../../lib/format';
import {
  dashboardWarningText,
  detectedEventLabel,
  eventStatusLabel,
  eventTypeLabel,
} from '../../lib/enumLabels';
import { useT } from '../../i18n';

export function OverviewPage() {
  const dashboard = useDashboard();
  const t = useT();

  return (
    <>
      <PageHeader title={t.overview.title} subtitle={t.overview.subtitle} />

      <QueryBoundary query={dashboard} loadingLabel={t.overview.loading}>
        {(data) => <Overview data={data} />}
      </QueryBoundary>
    </>
  );
}

function Overview({ data }: { data: DashboardResponse }) {
  const t = useT();
  const { coreAlliance, allianceGrowth } = data;

  if (!coreAlliance) {
    return (
      <EmptyState
        title={t.common.noCoreAllianceTitle}
        body={t.overview.noCoreAllianceBody}
        action={
          <Link className="btn btn--sm" to="/data/sync">
            {t.common.openSyncStatus}
          </Link>
        }
      />
    );
  }

  const memberCount =
    coreAlliance.memberCount === null
      ? null
      : t.overview.memberCount(
          formatNumber(coreAlliance.memberCount) ?? '',
          coreAlliance.memberMax ? formatNumber(coreAlliance.memberMax) : null,
        );

  return (
    <div className="stack">
      {data.warnings.length > 0 ? (
        <div className="notice-list">
          {/*
            Rebuilt from `code` + `params` rather than rendered from the
            server's pre-formatted `message`, which is English only. `message`
            stays as the fallback for a code this bundle does not know.
          */}
          {data.warnings.map((warning, index) => (
            <Notice key={`${warning.code}-${index}`} severity={warning.severity}>
              {dashboardWarningText(t, warning)}
            </Notice>
          ))}
        </div>
      ) : null}

      <div className="row">
        <h2>
          {coreAlliance.abbr ? `[${coreAlliance.abbr}] ` : ''}
          <Value absent={t.common.unnamedAlliance}>{coreAlliance.name}</Value>
        </h2>
        <FreshnessBadge label={t.freshness.sources.alliance} info={coreAlliance.freshness} />
      </div>

      <div className="grid grid--stats">
        <Stat
          label={t.overview.alliancePower}
          value={formatCompact(coreAlliance.power)}
          foot={<span className="faint">{t.common.observed}</span>}
          absentLabel={t.common.notSynced}
        />
        <Stat
          label={t.overview.stateRank}
          value={data.stateRank === null ? null : t.overview.stateRankValue(data.stateRank)}
          foot={<span className="faint">{t.overview.stateRankFoot}</span>}
          absentLabel={t.overview.stateRankAbsent}
        />
        <Stat label={t.common.members} value={memberCount} absentLabel={t.common.notSynced} />
        {/*
          "Not enough history" is not "no growth" and not zero: it says the
          system declined to compute a rate, which is a different claim about
          the alliance than a measured 0.
        */}
        <Stat
          label={t.overview.growth24h}
          value={allianceGrowth ? formatDelta(allianceGrowth.powerGrowth24h) : null}
          foot={<GrowthFoot growth={allianceGrowth} />}
          absentLabel={t.common.notEnoughHistory}
        />
        <Stat
          label={t.overview.growth7d}
          value={allianceGrowth ? formatDelta(allianceGrowth.powerGrowth7d) : null}
          foot={<GrowthFoot growth={allianceGrowth} />}
          absentLabel={t.common.notEnoughHistory}
        />
      </div>

      <div className="grid grid--halves">
        <Movers movers={data.movers} />
        <RecentActivity events={data.recentActivity} />
      </div>

      <UpcomingOperations events={data.upcomingEvents} />
    </div>
  );
}

function GrowthFoot({ growth }: { growth: DashboardResponse['allianceGrowth'] }) {
  const t = useT();
  if (!growth) return null;
  return (
    <span className="row" style={{ gap: 6 }}>
      <InferenceMark />
      <SampleCount
        samples={growth.sampleCount}
        noun={t.inference.nouns.snapshots}
        threshold={3}
      />
    </span>
  );
}

function Movers({ movers }: { movers: DashboardResponse['movers'] }) {
  const t = useT();
  return (
    <Card title={t.overview.moversTitle}>
      {movers.length === 0 ? (
        <p className="muted small" style={{ margin: 0 }}>
          {t.overview.moversEmpty}
        </p>
      ) : (
        <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none' }}>
          {movers.map((mover) => (
            <li key={mover.playerId} className="row" style={{ padding: '4px 0' }}>
              <Link to={`/players/${encodeURIComponent(mover.playerId)}`}>
                <Value absent={t.common.unnamedPlayer}>{mover.playerName}</Value>
              </Link>
              {mover.powerDelta !== null ? (
                <span className="nowrap" style={{ color: mover.powerDelta >= 0 ? 'var(--high)' : 'var(--low)' }}>
                  {formatDelta(mover.powerDelta)}
                </span>
              ) : null}
              {mover.tcFrom !== null && mover.tcTo !== null ? (
                <Badge tone="accent">
                  TC{mover.tcFrom} → TC{mover.tcTo}
                </Badge>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function RecentActivity({ events }: { events: DashboardResponse['recentActivity'] }) {
  const t = useT();
  return (
    <Card title={t.overview.recentActivityTitle}>
      {events.length === 0 ? (
        <p className="muted small" style={{ margin: 0 }}>
          {t.overview.recentActivityEmpty}
        </p>
      ) : (
        <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none' }}>
          {events.map((event) => (
            <li key={event.id} className="row" style={{ padding: '4px 0' }}>
              <Badge tone="neutral">{detectedEventLabel(t, event.eventType)}</Badge>
              {event.playerId ? (
                <Link to={`/players/${encodeURIComponent(event.playerId)}`}>
                  <Value absent={t.common.unnamedPlayer}>{event.playerName}</Value>
                </Link>
              ) : (
                <Value absent={t.common.unnamedPlayer}>{event.playerName}</Value>
              )}
              <span className="faint small nowrap">{formatDateTime(event.detectedAt)}</span>
              {event.confidence !== null && event.confidence < 0.8 ? (
                <Badge tone="MEDIUM" title={t.overview.detectedTitle}>
                  {t.overview.detectedBadge}
                </Badge>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function UpcomingOperations({ events }: { events: DashboardResponse['upcomingEvents'] }) {
  const t = useT();
  return (
    <Card title={t.overview.upcomingTitle}>
      {events.length === 0 ? (
        <p className="muted small" style={{ margin: 0 }}>
          {t.overview.upcomingEmpty}
        </p>
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>{t.overview.columns.operation}</th>
                <th>{t.overview.columns.name}</th>
                <th>{t.overview.columns.starts}</th>
                <th>{t.overview.columns.status}</th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <tr key={event.id}>
                  <td>{eventTypeLabel(t, event.eventType)}</td>
                  <td>
                    <Value absent={t.common.untitled}>{event.name}</Value>
                  </td>
                  <td>
                    <Value absent={t.overview.noStartTime}>{formatDateTime(event.startsAt)}</Value>
                  </td>
                  <td>
                    <Badge tone="neutral">{eventStatusLabel(t, event.status)}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
